import React, { useState, useRef, useEffect } from "react";
import YesFb from '../../image/thumbsup.png';
import NoFb from '../../image/thumbsdown.png';
import OMEGA from '../../omega';
import './inlinefeedback.scss';
import axios from "axios";
import { URLS } from "../../constants";

function InlineFeedback(props: any) {
  const [formData, setFormData] = useState({
    feedback: "",
    description: "",
  });
  const [showTextBox, setShowTextBox] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-focus textarea when it appears
  useEffect(() => {
    if (showTextBox && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [showTextBox]);

  const eventDefaultValue = {
    element: "Inline Feedback",
    type: "component",
    action: "click",
    widget: {
      name: "InlineFeedback",
      type: "component"
    },
    feature: "Inline Feedback Usage"
  }

  function omega_capture(element: any, widget_name: any, widget_type: any) {
    OMEGA.init().updateAndTrack("event", [{
      ...eventDefaultValue,
      element: element,
      widget: {
        name: widget_name,
        type: widget_type
      },
    }]);
  }

  const handleFeedbackClick = (choice: any) => {
    omega_capture("Inline Feedback - Helpful: " + choice, "Inline Feedback", "component");
    setFormData({ ...formData, feedback: choice });
    setShowTextBox(true);
  };

  const handleInputChange = (e: any) => {
    if (e.target.value.length <= 1000) {
      setFormData({ ...formData, description: e.target.value });
    }
  };

  const submitFeedback = (feedbackDescription: string) => {
    setIsSubmitting(true);
    setErrorMessage(false);

    const imsProfile: any = sessionStorage.getItem('adobeid_ims_profile');
    const profile: any = JSON.parse(imsProfile);

    const data: {
      helpful: string;
      feedback: string;
      adobe_id: string;
      email: string;
      conversation_id?: string;
    } = {
      helpful: String(formData.feedback || ''),
      feedback: String(feedbackDescription || ''),
      adobe_id: String(profile?.userId || ''),
      email: String(profile?.email || '')
    };

    if (props.conversationId) {
      data.conversation_id = String(props.conversationId);
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + window.adobeIMS.getAccessToken().token,
    };

    axios.post(URLS.FEEDBACK_URL, data, { headers })
      .then(response => {
        if (response.status >= 200 && response.status < 300) {
          setSubmitted(true);
          setIsSubmitting(false);
          if (props.onSubmitSuccess) {
            props.onSubmitSuccess();
          }
        } else {
          setErrorMessage(true);
          setIsSubmitting(false);
        }
      })
      .catch(error => {
        console.error("There was an error submitting the feedback!", error);
        omega_capture(
          `Error submitting inline feedback: ${error.message || error.toString()}`,
          "Inline Feedback",
          "error"
        );
        setErrorMessage(true);
        setIsSubmitting(false);
        if (props.onSubmitFailure) {
          props.onSubmitFailure();
        }
      });
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    omega_capture("Inline Feedback Submitted: Helpful: " + formData.feedback + " Description: " + formData.description, "Inline Feedback", "component");
    submitFeedback(formData.description);
  };

  const handleCancel = () => {
    omega_capture("Inline Feedback Cancelled - Submitting helpful data only", "Inline Feedback", "component");
    submitFeedback('');
  };

  // Render thumb buttons (reusable)
  const renderThumbButtons = (isInteractive: boolean = true) => (
    <div className="inline-feedback-buttons">
      <button
        className={`inline-feedback-thumb ${formData.feedback === "Yes" ? "selected" : ""}`}
        onClick={() => {
          if (isInteractive) {
            setFormData({ ...formData, feedback: "Yes" });
            omega_capture("Inline Feedback - Changed to Helpful: Yes", "Inline Feedback", "component");
          } else {
            handleFeedbackClick("Yes");
          }
        }}
        aria-label="Thumbs up"
        data-tooltip="Helpful"
      >
        <img src={YesFb} alt="Thumbs up" className="thumb-icon" />
      </button>

      <button
        className={`inline-feedback-thumb ${formData.feedback === "No" ? "selected" : ""}`}
        onClick={() => {
          if (isInteractive) {
            setFormData({ ...formData, feedback: "No" });
            omega_capture("Inline Feedback - Changed to Helpful: No", "Inline Feedback", "component");
          } else {
            handleFeedbackClick("No");
          }
        }}
        aria-label="Thumbs down"
        data-tooltip="Not helpful"
      >
        <img src={NoFb} alt="Thumbs down" className="thumb-icon" />
      </button>
    </div>
  );

  // If already submitted, show success message
  if (submitted) {
    return (
      <>
        <p className="feedback-success-message">Your feedback has been recorded. Thank you.</p>
      </>
    );
  }

  // If showing textbox (after thumb click)
  if (showTextBox) {
    return (
      <>
        {renderThumbButtons(true)}
        
        <div className="inline-feedback-form">
          <form onSubmit={handleSubmit}>
            <textarea
              ref={textareaRef}
              className="inline-feedback-textarea"
              placeholder="Tell us more about your experience (optional)"
              value={formData.description}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
            <span className="inline-feedback-charcount">
              {formData.description.length}/1000
            </span>
            {errorMessage && (
              <p className="feedback-error-message">
                We are unable to submit your feedback, please try again.
              </p>
            )}
            <div className="inline-feedback-actions">
              <button
                className="inline-feedback-submit"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
              <button
                className="inline-feedback-cancel"
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Cancel'}
              </button>
            </div>
          </form>
        </div>
      </>
    );
  }

  // Default: Show thumbs up/down
  return <>{renderThumbButtons(false)}</>;
}

export default InlineFeedback;
